import { type FC } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import { checkIsActive, KTIcon, type WithChildren } from '../../../../helpers';
import { useLayout } from '../../../core';
import type { SidebarHighlight } from '../../../../../app/config/menuConfig';
import './sidebar-menu-highlight.css';

type Props = {
  to: string;
  title: string;
  icon?: string;
  fontIcon?: string;
  hasBullet?: boolean;
  external?: boolean;
  highlight?: SidebarHighlight;
};

const SidebarMenuItem: FC<Props & WithChildren> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet = false,
  external = false,
  highlight,
}) => {
  const { pathname } = useLocation();
  const isActive = checkIsActive(pathname, to);
  const { config } = useLayout();
  const { app } = config;

  const showSvgIcon = !!icon && app?.sidebar?.default?.menu?.iconType === 'svg';
  const showFontIcon = !!fontIcon && app?.sidebar?.default?.menu?.iconType === 'font';

  const linkContent = (
    <>
      {!!hasBullet && (
        <span className="menu-bullet">
          <span className="bullet bullet-dot" />
        </span>
      )}
      {!!showSvgIcon && (
        <span className="menu-icon">
          {' '}
          <KTIcon className="fs-2" iconName={icon!} />
          {highlight?.variant === 'pulse' && <span className="menu-highlight-dot" />}
        </span>
      )}
      {!!showFontIcon && <i className={clsx('bi fs-3', fontIcon)} />}
      <span className="menu-title">{title}</span>
      {highlight?.variant === 'badge' && (
        <span className="badge badge-light-primary fs-9 fw-bold ms-auto">{highlight.label ?? 'Novo'}</span>
      )}
      {highlight?.variant === 'count' && (
        <span className="badge badge-circle badge-warning text-dark fs-9 fw-bold ms-auto">{highlight.label}</span>
      )}
    </>
  );

  return (
    <div className={clsx('menu-item', highlight && `menu-highlight menu-highlight-${highlight.variant}`)}>
      {external ? (
        <a className={clsx('menu-link without-sub')} href={to} rel="noopener noreferrer" target="_blank">
          {linkContent}
        </a>
      ) : (
        <Link className={clsx('menu-link without-sub', { active: isActive })} to={to}>
          {linkContent}
        </Link>
      )}
      {children}
    </div>
  );
};

export { SidebarMenuItem };
